/**
 * EntityQRCode — Canvas-based QR code generator
 *
 * Renders a QR code for an entity deep link. Uses the `qrcode` library
 * (28KB minified, zero deps) to draw onto a canvas element.
 *
 * Regenerates when `value` changes. Theme-aware via CSS vars.
 *
 * Usage:
 *   <EntityQRCode value="https://ferp.app/entity/abc-123" size={128} entityName="John Doe" />
 */

import { useEffect, useRef } from 'react';
import { Center, Text } from '@mantine/core';
import QRCode from 'qrcode';

interface EntityQRCodeProps {
  value: string;
  size?: number;
  entityName?: string;
}

export function EntityQRCode({
  value,
  size = 128,
  entityName,
}: EntityQRCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !value) return;

    // Read CSS var colors from computed style (theme-aware)
    const computedStyle = getComputedStyle(document.documentElement);
    const dark = computedStyle.getPropertyValue('--mantine-color-text').trim() || '#000000';
    const light = computedStyle.getPropertyValue('--mantine-color-body').trim() || '#ffffff';

    QRCode.toCanvas(canvasRef.current, value, {
      width: size,
      margin: 1,
      color: { dark, light },
      errorCorrectionLevel: 'M',
    }).catch((err) => {
      console.warn('[EntityQRCode] Failed to render QR code:', err);
    });
  }, [value, size]);

  if (!value) {
    return (
      <Center w={size} h={size}>
        <Text size="xs" c="dimmed">No link</Text>
      </Center>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      aria-label={entityName ? `QR code for ${entityName}` : 'QR code'}
      style={{ display: 'block' }}
    />
  );
}
